import { ModAlert, ModIconButton } from '@moderneinc/design-system-components';
import { Snackbar, Typography } from '@mui/material';
import { type FunctionComponent, useContext, useState } from 'react';
import { useConnectorVersionCheckQuery } from '../../../__generated__/apollo-hooks';
import { ConfigurationContext } from '../../../contexts/config.context';
import { useCapabilities } from '../../../hooks/use-capabilities.hooks';
import { CloseIcon, RestrictedIcon } from '../../../icons/icons';
import { useUserPreferenceStore } from '../../../stores/user-preference.store';

type ConnectorVersion = {
  major: number;
  minor: number;
  patch: number;
};

const convertVersionStringToVersion = (version: string): ConnectorVersion => {
  const [major, minor, patch] = version
    .split('.')
    .map((v) => Number.parseInt(v, 10));
  return { major, minor, patch };
};

const isTenantConnectorOutdated = (
  tenantConnector: string,
  latestConnector: string
): boolean => {
  const latest = convertVersionStringToVersion(latestConnector);
  const tenant = convertVersionStringToVersion(tenantConnector);
  if (tenant.major < latest.major) {
    return true;
  }
  if (tenant.major === latest.major && tenant.minor < latest.minor) {
    return true;
  }
  if (
    tenant.major === latest.major &&
    tenant.minor === latest.minor &&
    tenant.patch < latest.patch
  ) {
    return true;
  }
  return false;
};

export const UpdateConnector: FunctionComponent = () => {
  const { connector } = useCapabilities();
  const [open, setOpen] = useState<boolean>(true);

  const {
    config: { latestConnectorVersion }
  } = useContext(ConfigurationContext);
  const dismissedConnectorUpdates =
    useUserPreferenceStore((state) => state.dismissedConnectorUpdates) ?? [];
  const update = useUserPreferenceStore((state) => state.update);

  const { data } = useConnectorVersionCheckQuery({ skip: !connector });

  if (!connector) {
    return null;
  }

  const currentConnectorVersion = data?.connectors?.edges?.reduce(
    (prev, current) => {
      return prev > current.node.version ? prev : current.node.version;
    },
    ''
  );

  const isNewerVersion = Boolean(
    latestConnectorVersion &&
      currentConnectorVersion &&
      isTenantConnectorOutdated(
        currentConnectorVersion,
        latestConnectorVersion
      ) &&
      !dismissedConnectorUpdates?.includes(latestConnectorVersion)
  );

  const handleConnectorDismissal = () => {
    setOpen(false);
    dismissedConnectorUpdates.push(latestConnectorVersion);
    update({ dismissedConnectorUpdates });
  };

  return isNewerVersion ? (
    <Snackbar
      open={open}
      autoHideDuration={120000}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <ModAlert
        icon={<RestrictedIcon fontSize="small" />}
        type="filled"
        intent="info"
        title="Moderne Connector update available"
        description={`We see you're currently running ${currentConnectorVersion} of the Moderne Connector. Version ${latestConnectorVersion} is now available.`}
        action={
          <ModIconButton onClick={handleConnectorDismissal} size="small">
            <CloseIcon fontSize="small" />
          </ModIconButton>
        }
      >
        <Typography
          variant="caption"
          sx={{
            color: 'indigo.400'
          }}
        >
          Note: This is only visible to administrators.
        </Typography>
      </ModAlert>
    </Snackbar>
  ) : null;
};
